import type { ThemeConfig } from '../../themes/types';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { ManagerRegistry } from '../../core/manager-registry';
import type { DrawingPinRecord } from '../../types/events';
import { getIcon } from '../../utils/icon-manager';
import { BimTree } from '../tree';
import type { BimDialog } from '../dialog';
import type { TreeNodeConfig } from '../tree/types';
import type { ComponentTreeDrawerPinTabApi, ComponentTreeDrawerTabComponent } from './types';

interface PinTreeNode extends DrawingPinRecord {
    children?: PinTreeNode[];
}

export class ComponentTreeDrawerPinTab implements ComponentTreeDrawerTabComponent, ComponentTreeDrawerPinTabApi {
    public readonly element: HTMLElement;

    private readonly registry: ManagerRegistry | null;
    private readonly toolbarElement: HTMLElement;
    private readonly addPinBtn: HTMLButtonElement;
    private readonly addFolderBtn: HTMLButtonElement;
    private readonly emptyElement: HTMLElement;
    private readonly listContainer: HTMLElement;
    private tree: BimTree | null = null;
    private records: DrawingPinRecord[] = [];
    private nodes: PinTreeNode[] = [];
    private editingId: string | null = null;
    private deleteDialog: BimDialog | null = null;
    private editable = true;
    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeTheme: (() => void) | null = null;
    private unsubscribeListUpdated: (() => void) | null = null;

    constructor(registry: ManagerRegistry | null) {
        this.registry = registry;

        this.element = document.createElement('section');
        this.element.className = 'component-tree-drawer-page component-tree-drawer-pin-page';
        this.element.dataset.tab = 'pin';

        this.toolbarElement = document.createElement('div');
        this.toolbarElement.className = 'component-tree-drawer-pin-toolbar';

        const btnGroup = document.createElement('div');
        btnGroup.className = 'component-tree-drawer-pin-btn-group';

        this.addPinBtn = document.createElement('button');
        this.addPinBtn.type = 'button';
        this.addPinBtn.className = 'component-tree-drawer-add-pin-btn';
        this.addPinBtn.addEventListener('click', () => this.handleAddPin());

        this.addFolderBtn = document.createElement('button');
        this.addFolderBtn.type = 'button';
        this.addFolderBtn.className = 'component-tree-drawer-add-folder-btn';
        this.addFolderBtn.addEventListener('click', () => this.handleAddFolder());

        btnGroup.appendChild(this.addPinBtn);
        btnGroup.appendChild(this.addFolderBtn);
        this.toolbarElement.appendChild(btnGroup);

        this.emptyElement = document.createElement('div');
        this.emptyElement.className = 'component-tree-drawer-pin-empty';

        const emptyIcon = document.createElement('div');
        emptyIcon.className = 'component-tree-drawer-pin-empty-icon';
        emptyIcon.innerHTML = getIcon('pin');

        const emptyTitle = document.createElement('div');
        emptyTitle.className = 'component-tree-drawer-pin-empty-title';

        const emptyDesc = document.createElement('div');
        emptyDesc.className = 'component-tree-drawer-pin-empty-desc';

        this.emptyElement.appendChild(emptyIcon);
        this.emptyElement.appendChild(emptyTitle);
        this.emptyElement.appendChild(emptyDesc);

        this.listContainer = document.createElement('div');
        this.listContainer.className = 'component-tree-drawer-pin-tree-container';

        this.element.appendChild(this.toolbarElement);
        this.element.appendChild(this.emptyElement);
        this.element.appendChild(this.listContainer);
    }

    public init(): void {
        this.setLocales();
        this.setTheme(themeManager.getTheme());
        this.unsubscribeLocale = localeManager.subscribe(() => this.setLocales());
        this.unsubscribeTheme = themeManager.subscribe((theme) => this.setTheme(theme));
        this.unsubscribeListUpdated = this.registry?.on('drawingPin:list-updated', (payload) => {
            console.log('[PinTab] received drawingPin:list-updated:', payload);
            this.records = this.normalizeRecords(payload.records as DrawingPinRecord[]);
            this.nodes = this.buildTree(this.records);
            this.renderTree();
        }) ?? null;
    }

    public getElement(): HTMLElement {
        return this.element;
    }

    public setEditable(editable: boolean): void {
        if (this.editable === editable) return;
        this.editable = editable;

        if (!editable) {
            this.cancelEdit();
            this.closeDeleteDialog();
        } else if (this.tree) {
            this.renderTree();
        }

        this.syncEditableState();
    }

    public isEditable(): boolean {
        return this.editable;
    }

    public setTheme(theme: ThemeConfig): void {
        const style = this.element.style;
        style.setProperty('--bim-component-tree-page-title', theme.textPrimary);
        style.setProperty('--bim-component-tree-page-description', theme.textSecondary);
        style.setProperty('--bim-pin-primary', theme.primary);
        style.setProperty('--bim-pin-primary-hover', theme.primaryHover);
        style.setProperty('--bim-pin-bg', theme.bgElevated);
        style.setProperty('--bim-pin-card-bg', theme.componentBg);
        style.setProperty('--bim-pin-card-hover', theme.componentBgHover);
        style.setProperty('--bim-pin-border', theme.borderSubtle);
        style.setProperty('--bim-pin-text', theme.textPrimary);
        style.setProperty('--bim-pin-muted', theme.textSecondary);
        style.setProperty('--bim-pin-shadow', theme.shadowSm);
        style.setProperty('--bim-pin-shadow-hover', theme.shadowMd);
        style.setProperty('--bim-pin-soft', theme.primarySubtle);
    }

    public setLocales(): void {
        this.addPinBtn.innerHTML = `${getIcon('plus')} <span>${t('componentTreeDrawer.actions.addPin')}</span>`;
        this.addFolderBtn.innerHTML = `${getIcon('folder')} <span>${t('componentTreeDrawer.actions.addFolder')}</span>`;

        const emptyTitle = this.emptyElement.querySelector('.component-tree-drawer-pin-empty-title');
        const emptyDesc = this.emptyElement.querySelector('.component-tree-drawer-pin-empty-desc');
        if (emptyTitle) emptyTitle.textContent = t('componentTreeDrawer.empty.pinTitle');
        if (emptyDesc) emptyDesc.textContent = t('componentTreeDrawer.empty.pinDesc');
    }

    public destroy(): void {
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        if (this.unsubscribeListUpdated) {
            this.unsubscribeListUpdated();
            this.unsubscribeListUpdated = null;
        }
        this.closeDeleteDialog();
        this.tree?.destroy();
        this.tree = null;
        this.element.remove();
    }

    private normalizeRecords(records: DrawingPinRecord[]): DrawingPinRecord[] {
        return [...records].map((record) => ({
            ...record,
            parentId: record.parentId ?? null,
        }));
    }

    private buildTree(records: DrawingPinRecord[]): PinTreeNode[] {
        const nodeMap = new Map<string, PinTreeNode>();
        const roots: PinTreeNode[] = [];

        records.forEach((record) => {
            nodeMap.set(record.id, { ...record, children: [] });
        });

        records.forEach((record) => {
            const node = nodeMap.get(record.id);
            if (!node) return;

            const parentId = record.parentId ?? null;
            if (parentId && nodeMap.has(parentId)) {
                nodeMap.get(parentId)?.children?.push(node);
                return;
            }
            roots.push(node);
        });

        const sortDeep = (nodes: PinTreeNode[]) => {
            nodes.sort((a, b) => a.seq - b.seq || a.name.localeCompare(b.name));
            nodes.forEach((node) => {
                if (node.children?.length) {
                    sortDeep(node.children);
                } else {
                    delete node.children;
                }
            });
        };

        sortDeep(roots);
        return roots;
    }

    private handleAddPin(parentId: string | null = null): void {
        if (!this.editable) return;
        const engineComponent = this.registry?.engine3d?.getEngineComponent?.();
        if (!engineComponent) {
            console.warn('[PinTab] Engine component not available.');
            return;
        }
        engineComponent.createDrawingPin((data: any) => {
            console.log('[PinTab] createDrawingPin callback received:', data);
            const siblingCount = this.getSiblingCount(parentId, 'pin');
            const record: DrawingPinRecord = {
                id: `pin_${Date.now()}`,
                parentId,
                name: this.buildDefaultName('pin', siblingCount + 1),
                type: 'pin',
                seq: this.getNextSeq(parentId),
                data,
            };
            console.log('[PinTab] emitting drawingPin:create:', record);
            this.registry?.emit('drawingPin:create', record);
        });
    }

    private handleAddFolder(parentId: string | null = null): void {
        if (!this.editable) return;
        const siblingCount = this.getSiblingCount(parentId, 'folder');
        const record: DrawingPinRecord = {
            id: `folder_${Date.now()}`,
            parentId,
            name: this.buildDefaultName('folder', siblingCount + 1),
            type: 'folder',
            seq: this.getNextSeq(parentId),
        };
        console.log('[PinTab] emitting drawingPin:create (folder):', record);
        this.registry?.emit('drawingPin:create', record);
    }

    private buildDefaultName(type: DrawingPinRecord['type'], index: number): string {
        const key = type === 'pin'
            ? 'componentTreeDrawer.defaultNames.pin'
            : 'componentTreeDrawer.defaultNames.folder';
        return `${t(key)}${index}`;
    }

    private getNextSeq(parentId: string | null): number {
        return this.records.filter((record) => (record.parentId ?? null) === parentId).length;
    }

    private getSiblingCount(parentId: string | null, type: DrawingPinRecord['type']): number {
        return this.records.filter((record) => record.type === type && (record.parentId ?? null) === parentId).length;
    }

    private findNodeById(id: string, nodes: PinTreeNode[] = this.nodes): PinTreeNode | null {
        for (const node of nodes) {
            if (node.id === id) return node;
            if (node.children?.length) {
                const found = this.findNodeById(id, node.children);
                if (found) return found;
            }
        }
        return null;
    }

    private renderTree(): void {
        this.tree?.destroy();
        this.tree = null;

        if (this.nodes.length === 0) {
            this.emptyElement.style.display = 'flex';
            this.listContainer.style.display = 'none';
            return;
        }

        this.emptyElement.style.display = 'none';
        this.listContainer.style.display = 'block';

        this.tree = new BimTree({
            data: this.toTreeConfig(this.nodes),
            checkable: false,
            checkStrictly: false,
            defaultExpandAll: true,
            enableSearch: true,
            searchPlaceholder: 'tree.searchPlaceholder',
            renderActions: (config) => this.renderNodeActions(config.data as PinTreeNode),
            onNodeDblClick: (node) => {
                const data = node.config.data as PinTreeNode;
                if (data.type !== 'pin' || !data.data) return;
                const engineComponent = this.registry?.engine3d?.getEngineComponent?.();
                if (engineComponent) {
                    if (!this.editable) {
                        void engineComponent.restoreViewSnapshot(data.data);
                        return;
                    }
                    void engineComponent.restoreDrawingPin(data.data, (result: any) => {
                        this.notifyPinUpdated(data.id, { data: result });
                    });
                }
            },
        });

        this.tree.init();
        this.listContainer.innerHTML = '';
        this.listContainer.appendChild(this.tree.element);
        this.syncEditableState();
    }

    private toTreeConfig(nodes: PinTreeNode[]): TreeNodeConfig[] {
        return nodes.map((node) => ({
            id: node.id,
            label: node.name,
            icon: node.type === 'folder' ? getIcon('folder') : getIcon('pin'),
            expanded: true,
            data: node,
            children: node.children?.length ? this.toTreeConfig(node.children) : undefined,
            clickAction: node.type === 'folder' ? 'expand' : 'select',
        }));
    }

    private renderNodeActions(node: PinTreeNode): HTMLElement {
        const actions = document.createElement('div');
        actions.className = 'pin-node-actions';

        if (!this.editable) {
            actions.style.display = 'none';
            return actions;
        }

        if (node.type === 'folder') {
            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'pin-node-action-btn';
            editBtn.title = t('componentTreeDrawer.actions.edit');
            editBtn.setAttribute('aria-label', t('componentTreeDrawer.actions.edit'));
            editBtn.innerHTML = getIcon('edit');
            editBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.startEdit(node.id);
            });
            actions.appendChild(editBtn);

            const addPinBtn = document.createElement('button');
            addPinBtn.type = 'button';
            addPinBtn.className = 'pin-node-action-btn';
            addPinBtn.title = t('componentTreeDrawer.actions.addPin');
            addPinBtn.setAttribute('aria-label', t('componentTreeDrawer.actions.addPin'));
            addPinBtn.innerHTML = getIcon('plus');
            addPinBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleAddPin(node.id);
            });
            actions.appendChild(addPinBtn);

            const deleteFolderBtn = document.createElement('button');
            deleteFolderBtn.type = 'button';
            deleteFolderBtn.className = 'pin-node-action-btn';
            deleteFolderBtn.title = t('componentTreeDrawer.actions.delete');
            deleteFolderBtn.setAttribute('aria-label', t('componentTreeDrawer.actions.delete'));
            deleteFolderBtn.innerHTML = getIcon('delete');
            deleteFolderBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.openDeleteFolderDialog(node);
            });
            actions.appendChild(deleteFolderBtn);

            return actions;
        }

        const editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'pin-node-action-btn';
        editBtn.title = t('componentTreeDrawer.actions.edit');
        editBtn.setAttribute('aria-label', t('componentTreeDrawer.actions.edit'));
        editBtn.innerHTML = getIcon('edit');
        editBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.startEdit(node.id);
        });
        actions.appendChild(editBtn);

        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'pin-node-action-btn';
        deleteBtn.title = t('componentTreeDrawer.actions.delete');
        deleteBtn.setAttribute('aria-label', t('componentTreeDrawer.actions.delete'));
        deleteBtn.innerHTML = getIcon('delete');
        deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            console.log('[PinTab] emitting drawingPin:delete:', node);
            this.registry?.emit('drawingPin:delete', node as DrawingPinRecord);
        });
        actions.appendChild(deleteBtn);

        return actions;
    }

    private startEdit(id: string): void {
        if (!this.editable) return;
        if (this.editingId === id) return;
        this.editingId = id;

        const node = this.findNodeById(id);
        if (!node) return;

        const treeNode = this.tree?.getNode(id);
        if (!treeNode) return;

        const contentEl = treeNode.element.querySelector('.bim-tree-node-content') as HTMLElement | null;
        if (!contentEl) return;

        const titleEl = contentEl.querySelector('.bim-tree-title') as HTMLElement | null;
        const actionsEl = contentEl.querySelector('.bim-tree-node-actions') as HTMLElement | null;
        if (!titleEl || !actionsEl) return;

        titleEl.style.display = 'none';
        actionsEl.style.display = 'none';

        const editWrapper = document.createElement('div');
        editWrapper.className = 'pin-node-edit-wrapper';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'pin-node-edit-input';
        input.value = node.name;
        input.maxLength = 12;

        const editActions = document.createElement('div');
        editActions.className = 'pin-node-edit-actions';

        const saveBtn = document.createElement('button');
        saveBtn.type = 'button';
        saveBtn.className = 'pin-node-edit-btn pin-node-edit-save';
        saveBtn.innerHTML = getIcon('check');
        saveBtn.title = t('componentTreeDrawer.actions.save');
        saveBtn.setAttribute('aria-label', t('componentTreeDrawer.actions.save'));
        saveBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.saveEdit(id, input.value);
        });

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'pin-node-edit-btn pin-node-edit-cancel';
        cancelBtn.innerHTML = getIcon('close');
        cancelBtn.title = t('componentTreeDrawer.actions.cancel');
        cancelBtn.setAttribute('aria-label', t('componentTreeDrawer.actions.cancel'));
        cancelBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.cancelEdit();
        });

        editActions.appendChild(saveBtn);
        editActions.appendChild(cancelBtn);
        editWrapper.appendChild(input);
        editWrapper.appendChild(editActions);
        contentEl.appendChild(editWrapper);

        requestAnimationFrame(() => {
            input.focus();
            input.select();
        });

        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.saveEdit(id, input.value);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.cancelEdit();
            }
        };
        input.addEventListener('keydown', handleKeydown);

        (contentEl as HTMLElement & { _editWrapper?: HTMLElement; _keydownHandler?: (e: KeyboardEvent) => void })._editWrapper = editWrapper;
        (contentEl as HTMLElement & { _editWrapper?: HTMLElement; _keydownHandler?: (e: KeyboardEvent) => void })._keydownHandler = handleKeydown;
    }

    private saveEdit(id: string, newName: string): void {
        const node = this.findNodeById(id);
        if (!node) return;

        const trimmedName = newName.trim() || node.name;
        const finalName = trimmedName.slice(0, 12);

        if (finalName === node.name) {
            this.cancelEdit();
            return;
        }

        this.notifyPinUpdated(id, { name: finalName });
        this.editingId = null;
    }

    private notifyPinUpdated(id: string, updates: Partial<DrawingPinRecord> = {}): void {
        const node = this.findNodeById(id);
        if (!node) return;

        const record: DrawingPinRecord = {
            id: node.id,
            parentId: node.parentId,
            name: updates.name ?? node.name,
            type: node.type,
            seq: updates.seq ?? node.seq,
            data: updates.data ?? node.data,
        };

        console.log('[PinTab] emitting drawingPin:update with full record:', record);
        this.registry?.emit('drawingPin:update', record);
    }

    private cancelEdit(): void {
        this.editingId = null;
        this.renderTree();
    }

    private countPins(nodes: PinTreeNode[]): number {
        let total = 0;
        nodes.forEach((node) => {
            if (node.type === 'pin') total += 1;
            if (node.children?.length) {
                total += this.countPins(node.children);
            }
        });
        return total;
    }

    private openDeleteFolderDialog(node: PinTreeNode): void {
        if (!this.editable) return;
        this.closeDeleteDialog();

        const content = document.createElement('div');
        content.className = 'pin-delete-dialog';

        const icon = document.createElement('div');
        icon.className = 'pin-delete-dialog-symbol';
        icon.textContent = '!';

        const message = document.createElement('div');
        message.className = 'pin-delete-dialog-text';
        message.textContent = `${t('componentTreeDrawer.dialogs.deleteFolderMessagePrefix')}${node.name}${t('componentTreeDrawer.dialogs.deleteFolderMessageSuffix')}`;

        const actions = document.createElement('div');
        actions.className = 'pin-delete-dialog-actions';

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'pin-delete-dialog-btn is-secondary';
        cancelBtn.textContent = t('componentTreeDrawer.actions.cancel');

        const confirmBtn = document.createElement('button');
        confirmBtn.type = 'button';
        confirmBtn.className = 'pin-delete-dialog-btn is-danger';
        confirmBtn.textContent = t('componentTreeDrawer.dialogs.confirm');

        cancelBtn.addEventListener('click', () => this.closeDeleteDialog());
        confirmBtn.addEventListener('click', () => {
            console.log('[PinTab] emitting drawingPin:delete (folder):', node);
            this.registry?.emit('drawingPin:delete', node as DrawingPinRecord);
            this.closeDeleteDialog();
        });

        actions.appendChild(cancelBtn);
        actions.appendChild(confirmBtn);
        content.appendChild(icon);
        content.appendChild(message);
        content.appendChild(actions);

        this.deleteDialog = this.registry?.dialog?.create({
            title: t('componentTreeDrawer.dialogs.noticeTitle'),
            content,
            width: 360,
            height: 'auto',
            position: 'center',
            draggable: false,
            resizable: false,
        }) ?? null;
    }

    private closeDeleteDialog(): void {
        if (!this.deleteDialog) return;
        this.deleteDialog.close();
        this.deleteDialog = null;
    }

    private syncEditableState(): void {
        this.toolbarElement.style.display = this.editable ? 'flex' : 'none';
        this.element.dataset.editable = String(this.editable);
    }
}
